/* eslint-disable no-bitwise */
/* eslint-disable radix */
import Cookies from 'js-cookie';
import Ping from 'ping.js';
import md5 from 'md5';
import config from '@/config/index';
// eslint-disable-next-line import/no-cycle
import { hasOneOf, isStaffVersion, checkCustomizedVersion } from './tools';
import Base64 from 'js-base64';
import Vue from 'vue';
import { get, random } from 'lodash';
import { LANGUAGES, TRANSLATE_LANUAGES, UNCOMPRESSED_FORMAT_TYPE, SS_SHARE_CHANNEL_ACCESS_TOKEN_KEY, THIRD_PARTY_SSO_K_I } from '@/constants/common';
import dayjs from 'dayjs';
import { getTimeZone } from '@/locales/timeZone';
import { isClient } from './client';
import Compressor from 'compressorjs';
import { utils } from '@salesmartly/tools';

const { notLoginPage } = config;

export const clearLoginStatus = () => {
    window.localStorage.removeItem(config.tokenKey);
    const cookies = document.cookie.split(';');
    cookies.map((item: string) => {
        const eqPos = item.indexOf('=');
        const name = eqPos > -1 ? item.substr(0, eqPos) : item;
        Cookies.remove(name);
        return item;
    });
};

/**
 * @param err - 要检查的错误
 * @return 错误是否为 QuotaExceededError？
 */
const isQuotaExceededError = (err: unknown) => {
    return (
        err instanceof DOMException &&
      (err.code === 22 ||
        err.code === 1014 ||
        err.name === 'QuotaExceededError' ||
        err.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    );
};

/**
 * 判断 Web Storage API（localStorage 或 sessionStorage）的存储是否受支持
 * @param type - 要检查的 Web Storage API
 * @return 存储是否受支持
 */
const isStorageSupported = (type: 'localStorage' | 'sessionStorage') => {
    let storage: Storage | undefined;
    try {
        storage = window[type];
        if (!storage) {
            return false;
        }
        const _key = '__ss_storage_test__';
        storage.setItem(_key, _key);
        storage.removeItem(_key);
        return true;
    } catch (err) {
        // 只有在已经存储了某些内容时才承认 QuotaExceededError。
        const isValidQuotaExceededError = isQuotaExceededError(err) && storage && storage.length > 0;
        return isValidQuotaExceededError;
    }
};

const supportLocalStorage = isStorageSupported('localStorage');

// 清理缓存key值
export const clearLocalStorageKeys = () => {
    const clearStatus = localStorage.getItem('CLEAR_CACHE_STATUS');
    if (clearStatus === '1') return;

    const start = Date.now();
    const data = {
        s_len: localStorage.length,
        e_len: 0,
        t: 0,
        k_long: [] as string[],
    };
    // 清理编辑器冗余缓存 TODO: 优化清理逻辑，一段时间后删除该段代码
    for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i) || '';
        if (key.indexOf('tinymce-autosave') > -1) {
            localStorage.removeItem(key);
        }
        if (data.k_long.length < 500) {
            data.k_long.push(key);
        }
    }
    data.t = Date.now() - start;
    data.e_len = localStorage.length;
    console.log(['LOCAL_STORAGE_CLEAR_KEYS', data]);
    if (Vue.prototype && Vue.prototype.$log) {
        localStorage.setItem('CLEAR_CACHE_STATUS', '1');
        Vue.prototype.$log('SS_ERROR', 'LOCAL_STORAGE_CLEAR_KEYS', { data });
    }
};

/**
 * 获取缓存大小
 * @returns 大小 byte
 */
const getLocalStorageSize = () => {
    let size = 0;
    const longKeys = [];
    if (supportLocalStorage) {
        // 遍历localStorage中所有的items
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i) || '';
            const value = localStorage.getItem(key) || '';
            // 计算键和值的大小
            size += key.length + ((value && value.length) || 0);
            if (value && value.length > 500) {
                longKeys.push({
                    key,
                    size: value.length,
                });
            }
        }
    }
    // 返回大小
    return {
        size,
        longKeys,
    };
};

// console.log('supportLocalStorage', supportLocalStorage);

export const localSave = (key: string, value: string) => {
    try {
        if (supportLocalStorage) {
            localStorage.setItem(key, value);
        } else {
            // 不支持缓存
            Vue.prototype.$log('SS_ERROR', 'LOCAL_STORAGE_NOT_SUPPORT', { data: { key, supportLocalStorage } });
        }
    } catch (e) {
        console.error(e);
        const storage = window.localStorage || '';
        const data = {
            e,
            key,
            supportLocalStorage,
            isQuotaExceededError: isQuotaExceededError(e),
            length: storage.length,
            ...getLocalStorageSize(),
        };
        // 清理缓存
        clearLocalStorageKeys();
        console.log(['LOCAL_STORAGE_SAVE', data]);
        Vue.prototype.$log('SS_ERROR', 'LOCAL_STORAGE_SAVE', { data });
    }
};

export const localRead = (key: string) => localStorage.getItem(key) || '';

export const sessionSave = (key: string, value: string) => {
    try {
        sessionStorage.setItem(key, value);
    } catch (e) {
        console.error(e);
        Vue.prototype.$log('SS_ERROR', 'SESSION_STORAGE_SAVE', { data: { e, key } });
    }
};

export const sessionRead = (key: string) => sessionStorage.getItem(key) || '';

export const sessionRemove = (key: string) => {
    sessionStorage.removeItem(key);
};

// tab页生成唯一标识符
export const createSessionKey = () => {
    const tabKey = `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    if (!sessionRead('SESSION_TAB_KEY')) {
        sessionSave('SESSION_TAB_KEY', tabKey);
    }
};

export const getCookie = () => document.cookie;

/**
 * @desc 用于清除某个localStorage键值对
 * @param key 要清除的localStorage键
 */
export const localRemove = (key: string) => {
    localStorage.removeItem(key);
};

export const isNotLoginPage = (name?: string) => notLoginPage.some((item: any) => item === name);

export const getValueByPath = (obj: any, path: string) => {
    const reg = /(?:(?:^|.)([^.[\]]+))|(?:\[([^[\]]+)\])/g;
    const names: any[] = [];
    let name = reg.exec(path);
    while (name != null) {
        names.push(name[1] || name[2]);
        name = reg.exec(path);
    }
    let o = obj;
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < names.length; i += 1) {
        o = o[names[i]];
        if (o === undefined) {
            return undefined;
        }
    }
    return o;
};

export const hasChild = (item: any) => item.children && item.children.length !== 0;

export const showThisMenuEle = (item: any, access: any) => {
    if (item.meta && item.meta.access && item.meta.access.length) {
        if (hasOneOf(item.meta.access, access)) {
            return true;
        }
        return false;
    }

    // 当为员工版时，不显示帮助中心
    // 因员工版的判断因素为，不为超管且具有STAFF_VERSION权限，与目前的设计不符
    // 临时方案在菜单显示这里加一段判断逻辑
    if (item && item.path === '/help-center' && isStaffVersion()) {
        return false;
    }

    return true;
};

export const getMenuByRouter = (list: any[], access: any[], isBottomMenu = false): Array<[]> => {
    return list.filter((item: any) => {
        const itemAccess = item?.meta?.access || [];
        // hideInMenu 为 false, 且有权限
        return item.meta && !item.meta.hideInMenu && (!itemAccess.length || hasOneOf(itemAccess, access));
    });
};

export const toThousandsString = (num: number | string) => {
    if (num === undefined) { return '0'; }
    let isF = false;
    let numS = num.toString();
    if (numS.indexOf('-') !== -1) {
        numS = numS.replace('-', '');
        isF = true;
    }
    const splitNumber = numS.split('.');
    let integer = splitNumber[0];
    const decimal = splitNumber[1] || '';
    let result = '';
    while (integer.length > 3) {
        result = `,${integer.slice(-3)}${result}`;
        integer = integer.slice(0, integer.length - 3);
    }
    if (integer) {
        result = integer + result;
    }
    if (isF) {
        result = `-${result}`;
    }
    if (decimal === '') {
        return result;
    }
    return `${result}.${decimal}`;
};

/**
 * @param {*} access 用户权限数组，如 ['super_admin', 'admin']
 * @param {*} route 路由列表
 */
export const hasAccess = (access: any, route: any) => {
    if (route.meta && route.meta.access) {
        return hasOneOf(access, route.meta.access);
    }

    // 当为员工版时，不显示帮助中心
    // 因员工版的判断因素为，不为超管且具有STAFF_VERSION权限，与目前的设计不符
    // 临时方案在访问权限这里加一段判断逻辑
    if (route.path === '/help-center' && isStaffVersion()) {
        return false;
    }

    // 特殊定制版，不能访问移动端页面
    if (route.name === 'authMode_setting_mobile' && checkCustomizedVersion()) {
        return false;
    }

    return true;
};

/**
 * 权鉴
 * @param {*} name 即将跳转的路由name
 * @param {*} access 用户权限数组
 * @param {*} routes 路由列表
 * @description 用户是否可跳转到该页
 */
export const canTurnTo = (name: string, access: any, routes: any) => {
    const routePermissionJudge = (list: any) => list.some((item: any) => {
        if (item.name === name) {
            return hasAccess(access, item);
        }
        if (item.children && item.children.length) {
            return routePermissionJudge(item.children);
        }
        return false;
    });
    return routePermissionJudge(routes);
};

export const firstToUp = (key: string): string => {
    const arr: string[] = key.split('');
    const first = arr[0].toLocaleUpperCase();
    arr.splice(0, 1);
    return first + arr.join('');
};

export const changeKeyToJump = (key: string, separator: string = '_'): string => {
    const arr: string[] = key.split(separator);
    let newKey: string = '';
    arr.map((item: string, index: number) => {
        if (index > 0) {
            newKey += firstToUp(item);
        } else {
            newKey += item;
        }
        return item;
    });
    return newKey;
};

export interface ImageSize {
    width: number;
    height: number;
    size: number;
    src?: string;
}

export const getImageSize = (file: File): Promise<ImageSize> => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
        // 加载图片获取图片真实宽度和高度
        const image: any = new Image();
        image.addEventListener('load', () => {
            window.console.dir(image);
            const { width, height } = image;
            resolve({
                width,
                height,
                size: file.size,
                src: image.src,
            });
        }, false);
        image.src = e.target.result;
        if (image.complete) {
            const { width, height } = image;
            resolve({
                width,
                height,
                size: file.size,
                src: e.target.result,
            });
        }
    };
    reader.readAsDataURL(file);
});

export const getFormatTime = (nowTime: number) => {
    // 获取今天零点时间
    const getTodayUnix = () => {
        const date = new Date();
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date.getTime();
    };

    // 获取今年的1月1日零点时间
    const getYearUnix = () => {
        const date = new Date();
        date.setMonth(0);
        date.setDate(1);
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date.getTime();
    };

    // 获取标准时间
    const getLastDate = (time: number) => {
        const date = new Date(time);
        const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
        const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        return `${date.getFullYear()}-${month}-${day}`;
    };

    const text = {
        en: {
            just: 'Just',
            minutes: ' minutes ago',
            hours: ' hours ago',
            day: ' days ago',
        },
        cn: {
            just: '刚刚',
            minutes: '分钟前',
            hours: '小时前',
            day: '天前',
        },
    };

    const lang = window.localStorage.getItem('local') === 'zh-CN' ? 'cn' : 'en';

    // 转换时间
    const getFormatTimeTip = (timestamp: number) => {
        const now = new Date().getTime();
        const today = getTodayUnix();
        const timer = (now - timestamp) / 1000;
        let tip = '';
        if (timer <= 0) {
            tip = text[lang].just;
        } else if (Math.floor(timer / 60) <= 0) {
            tip = text[lang].just;
        } else if (timer < 3600) {
            tip = `${Math.floor(timer / 60)}${text[lang].minutes}`;
        } else if (timer >= 3600 && (timestamp - today >= 0)) {
            tip = `${Math.floor(timer / 3600)}${text[lang].hours}`;
        } else if (timer / 86400 <= 31) {
            tip = `${Math.ceil(timer / 86400)}${text[lang].day}`;
        } else {
            tip = getLastDate(timestamp);
        }
        return tip;
    };
    return getFormatTimeTip(nowTime);
};

// 获取 url 的参数
export const getQueryString = (name: string, url?: string) => {
    const urlstr = url || window.location.search;
    return utils.getQueryString(name, urlstr);
};

/**
 * @description 本地存储和获取标签导航列表
 */
export const setTagNavListInLocalStorage = (userId: string, list: any[]) => {
    localSave(`tagNaveList_${userId}`, JSON.stringify(list));
};

/**
 * @returns {Array} 其中的每个元素只包含路由原信息中的name, path, meta三项
 */
export const getTagNavListFromLocalStorage = (userId: string, routes: any[], access: string[]): any[] => {
    const list = localStorage.getItem(`tagNaveList_${userId}`);
    let tabLists: any[] = list ? JSON.parse(list) : [];
    if (tabLists.length === 0) {
        return tabLists;
    }

    function checkRouteName(routeList: any[], name: string): boolean {
        let isHas = false;
        for (const i in routeList) {
            if (routeList[i].children) {
                isHas = checkRouteName(routeList[i].children, name);
                if (isHas) {
                    break;
                }
            } else if (routeList[i].name === name) {
                isHas = true;
                break;
            }
        }

        return isHas;
    }
    tabLists = tabLists.filter((item: any) => {
        const isAccess = hasAccess(access, item);
        const isHas = checkRouteName(routes, item.name);
        return isHas && isAccess;
    });
    return tabLists;
};

/**
 * @param {*} list 现有标签导航列表
 * @param {*} newRoute 新添加的路由原信息对象
 * @description 如果该newRoute已经存在则不再添加
 */
export const getNewTagList = (list: any[], newRoute: any) => {
    const {
        name, path, meta, query, params,
    } = newRoute;
    const newList = [...list];
    if (name.indexOf('adManageFacebook') === -1) {
        const index = newList.findIndex((item: any) => item.name === name);
        if (index >= 0) {
            newList[index].query = query;
            newList[index].params = params;
            return newList;
        }
    } else {
        // 针对广告管理做特殊判断
        const index = newList.findIndex((item: any) => item.name.indexOf('adManageFacebook') === 0);

        // 如果存在，替换保存的路由
        if (index > -1) {
            newList[index] = {
                name,
                path,
                meta,
                query,
                params,
            };

            return newList;
        }
    }

    newList.push({
        name, path, meta, query, params,
    });

    return newList;
};
export const LightenDarkenColor = (col: string, amt: number) => {
    let usePound = false;
    let color = col;

    if (col[0] === '#') {
        color = col.slice(1);
        usePound = true;
    }

    const num = parseInt(color, 16);

    // tslint:disable-next-line: no-bitwise
    let r = (num >> 16) + amt;

    if (r > 255) {
        r = 255;
    } else if (r < 0) {
        r = 0;
    }

    // tslint:disable-next-line: no-bitwise
    let b = ((num >> 8) && 0x00FF) + amt;

    if (b > 255) {
        b = 255;
    } else if (b < 0) {
        b = 0;
    }

    let g = (num && 0x0000FF) + amt;

    if (g > 255) {
        g = 255;
    } else if (g < 0) {
        g = 0;
    }

    // tslint:disable-next-line: no-bitwise
    return (usePound ? '#' : '') + String(`000000${(g | (b << 8) | (r << 16)).toString(16)}`).slice(-6);
};
export const getContrastYIQ = (colorRGB: string) => {
    const colors = colorRGB.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/) as any;
    const red = colors[1];
    const green = colors[2];
    const blue = colors[3];
    const brightness = (red * 299) + (green * 587) + (blue * 114);
    const brightnessVal = brightness / 255000;
    if (brightnessVal >= 0.7) {
        return 'light';
    }
    return 'dark';
};

export const getIdColor = (id: string) => {
    const md5Key = md5(id);
    const val = `#${md5Key.substr(-6)}`;
    const sColorChange = [];
    for (let i = 1; i < 7; i += 2) {
        // tslint:disable-next-line: radix
        sColorChange.push(parseInt(`0x${val.slice(i, i + 2)}`));
    }
    const colorRGB = `rgb(${sColorChange.join(',')})`;
    return getContrastYIQ(colorRGB) === 'dark' ? val : LightenDarkenColor(val, -80);
};

export const getElectronVersion = () => {
    const arr = window.navigator.userAgent.split(' ');
    const adspower = arr.filter((e: string) => e.indexOf('adspower') > -1);
    if (adspower.length) {
        return adspower[0].replace('adspower/', '');
    }
    return '';
};

export const version2Num = (version: string) => {
    const temp = version.replace(/[a-zA-Z]/g, '');
    const arr = temp.split('.');
    const num = ['0000', '000', '00', '0', ''];
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < arr.length; i += 1) {
        const len = arr[i].length;
        if (i > 0) {
            arr[i] = num[len] + arr[i];
        }
    }
    return arr.join('');
};

export const versionDiff = (curVersion: string, supportVersion: string) => {
    if (!curVersion) {
        return false;
    }
    const curTemp = curVersion.replace(/[a-zA-Z]/g, '');
    const supportTemp = supportVersion.replace(/[a-zA-Z]/g, '');
    if (curTemp === supportTemp) {
        return true;
    }
    const curArr = curTemp.split('.');
    const supportArr = supportTemp.split('.');
    if (curArr[0] > supportArr[0]) {
        return true;
    }
    if (curArr[1] > supportArr[1]) {
        return true;
    }
    if (curArr[2] > supportArr[2]) {
        return true;
    }
    if (curArr.length === 4) {
        if (curArr[3] > supportArr[3]) {
            return true;
        }
    }
    if (curArr.length === 5) {
        if (curArr[4] > supportArr[4]) {
            return true;
        }
    }
    return false;
};

export const greaterThanVersion = (curVersion: string, supportVersion: string): boolean => {
    if (!curVersion) {
        return false;
    }
    if (!supportVersion) {
        return false;
    }
    const curTemp = curVersion.replace(/[a-zA-Z]/g, '');
    const supportTemp = supportVersion.replace(/[a-zA-Z]/g, '');
    if (curTemp === supportTemp) {
        return true;
    }
    const curArr = curTemp.split('.');
    const supportArr = supportTemp.split('.');
    for (let i = 0; i < curArr.length; i += 1) {
        if (+curArr[i] !== +supportArr[i]) {
            return +curArr[i] > +supportArr[i];
        }
    }
    return false;
};

// scrollTop animation
export const scrollTop = (el: any, from: number = 0, to: number, duration: number = 500, endCallback?: () => void) => {
    if (!window.requestAnimationFrame) {
        const fun = (callback: any) => window.setTimeout(callback, 1000 / 60);
        window.requestAnimationFrame = (
            (window as any).webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.msRequestAnimationFrame || fun
        );
    }
    const difference = Math.abs(from - to);
    const stepNum = Math.ceil((difference / duration) * 50);

    function scroll(start: number, end: number, step: number) {
        if (start === end) {
            if (endCallback) {
                endCallback();
            }
            return;
        }

        let d = (start + step > end) ? end : start + step;
        if (start > end) {
            d = (start - step < end) ? end : start - step;
        }

        if (el === window) {
            window.scrollTo(d, d);
        } else {
            el.scrollTop = d;
        }
        window.requestAnimationFrame(() => scroll(d, end, step));
    }
    scroll(from, to, stepNum);
};

// 校验IPv4或IPv6，正则表达式来自w3cschool
export const validateIPv4OrIPv6 = (ip: string) => {
    // eslint-disable-next-line
    const reg = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$|^([\da-fA-F]{1,4}:){6}((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$|^::([\da-fA-F]{1,4}:){0,4}((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$|^([\da-fA-F]{1,4}:):([\da-fA-F]{1,4}:){0,3}((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$|^([\da-fA-F]{1,4}:){2}:([\da-fA-F]{1,4}:){0,2}((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$|^([\da-fA-F]{1,4}:){3}:([\da-fA-F]{1,4}:){0,1}((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$|^([\da-fA-F]{1,4}:){4}:((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$|^([\da-fA-F]{1,4}:){7}[\da-fA-F]{1,4}$|^:((:[\da-fA-F]{1,4}){1,6}|:)$|^[\da-fA-F]{1,4}:((:[\da-fA-F]{1,4}){1,5}|:)$|^([\da-fA-F]{1,4}:){2}((:[\da-fA-F]{1,4}){1,4}|:)$|^([\da-fA-F]{1,4}:){3}((:[\da-fA-F]{1,4}){1,3}|:)$|^([\da-fA-F]{1,4}:){4}((:[\da-fA-F]{1,4}){1,2}|:)$|^([\da-fA-F]{1,4}:){5}:([\da-fA-F]{1,4})?$|^([\da-fA-F]{1,4}:){6}:$/;
    return reg.test(ip);
};

export const sleep = (second: number) => new Promise((resolve: any) => setTimeout(resolve, second));

export const getPing = (url: string) => new Promise((resolve) => {
    const netTool = new Ping({
        timeout: 5000,
    });
    netTool.ping(url, (err: string, data: number) => {
        if (err) {
            resolve({
                url,
                ping: 5000,
            });
            return;
        }
        resolve({
            url,
            ping: Math.round(data / 10),
        });
    });
});

export const encodeBase64 = utils.encodeBase64;

// 替换加速域名
export const replaceAcceleratedDomain = utils.replaceAcceleratedDomain;

// 获取url后缀名
export const getImageExtension = utils.getImageExtension;

// 添加OSS图片处理参数
export const getOSSThumbUrl = utils.getOSSThumbUrl;

// 头像缩略图
export const getAvatarThumburl = utils.getAvatarThumburl;

// 转换gif缩略图
export const conversionGif = utils.conversionGif;

/**
 * @description 是否 a 标签包裹文本
 * @param text
 */
export const isWrappedWithATag = (text: string) => {
    // 正则表达式匹配 <a> 标签及其内容
    const aTagReg = /^<a\s+[^>]*>(.*?)<\/a>$/i;
    return aTagReg.test(text);
};

// 匹配网址，添加 a 标签，并新标签页打开
export const replaceLink = utils.replaceLink;

/**
 * 匹配网址，添加 a 标签，并新标签页打开，处理正则匹配性能问题
 * @param text 匹配内容
 * @returns {String} text
 */
export const standbyReplaceLink = (text: string) => {
    const len = text.length;
    const urlMatch: number[][] = [];
    let urlStartCursor = -1;
    let urlEndCursor = -1;
    for (let i = 0; i < len; i += 1) {
        for (let j = i; j < len; j += 1) {
            // 要放在这里，否则正则会有之前的记录
            // eslint-disable-next-line no-useless-escape
            const _urlReg = /^((https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]+(:\d+)?([\/\?#][\S]*)?)$/g;
            const str = text.slice(i, j + 1);
            if (_urlReg.test(str)) {
                // i > urlEndCursor 是为了下次匹配必须要在上一个结束指针之后
                if (urlStartCursor === -1 && i > urlEndCursor) {
                    urlStartCursor = i;
                } else {
                    urlEndCursor = j + 1;
                }
            }

            if (j === len - 1 && urlStartCursor > -1) {
                urlMatch.push([urlStartCursor, urlEndCursor]);
                urlStartCursor = -1;
            }
        }
    }

    const replaceText = [];
    urlMatch.forEach((arr, i) => {
        if (i === 0) {
            replaceText.push(text.slice(0, arr[0]));
        } else {
            replaceText.push(text.slice(urlMatch[i - 1][1], arr[0]));
        }

        // 替换
        const url = text.slice(arr[0], arr[1]);
        const hasProtocol = /^https?:\/\//.test(url);
        const urlElem = `<a href="${hasProtocol ? '' : '//'}${url}" target="_blank">${url}</a>`;
        replaceText.push(urlElem);
    });
    // 如果有匹配到
    if (urlMatch.length) {
        const lastStr = text.slice(urlMatch[urlMatch.length - 1][1]);
        replaceText.push(lastStr);
        return replaceText.join('');
    }

    return text;
};

const emailReg = /([A-Za-z\d]+([-_.][A-Za-z\d]+)*@([A-Za-z\d]+[-.])+[A-Za-z\d]{2,12})/g;
// 匹配邮箱，添加 a 标签，唤起系统默认邮箱客户端打开
export const replaceEmail = (text: string) => text.replace(emailReg, (match: any, p1: string) => {
    // 检查 email 前后是否已经被 a 标签包裹
    const aTagReg = new RegExp(`<a[^>]*?>[^<]*${match}[^<]*</a>`, 'i');
    if (!aTagReg.test(text)) {
        return `<a href="mailto:${match}">${match}</a>`;
    }

    return match;
});

// 只能 bind 在实例中使用
export const onAvatarError = function onAvatarError(item: any, key: string, isUpdate: boolean = true, callback: Function = () => {}) {
    Vue.prototype.$log('SS_IMAGE', 'AVATAR_ERROR', { data: { error_url: get(item, key) } });
    if (isUpdate) {
        Vue.set(item, key, '');
    } else {
        callback();
    }
};

// 获取翻译表中对应的 language code
export const getTranslateLang = (language: string, hasDefault = true) => {
    const CURRENT_TRANSLATE_LANUAGES = [...TRANSLATE_LANUAGES, 'zh-HK', 'zh_TW', 'zh_HK'];
    if (!CURRENT_TRANSLATE_LANUAGES.includes(language)) {
        if (language) {
            if (language.includes('-')) {
                language = language.split('-')[0];
            } else if (language.includes('_')) {
                language = language.split('_')[0];
            }
            if (!CURRENT_TRANSLATE_LANUAGES.includes(language)) {
                language = 'en';
            }
        } else if (hasDefault) {
            language = 'en';
        }
    }
    return language;
};

// 获取语言 i18n
export const getLangI18n = (language: string, hasDefault = true) => {
    const lang = getTranslateLang(language, hasDefault);
    const currentLanguages = [...LANGUAGES, 'ceb', 'haw', 'hmn', 'zh-TW', 'zh-HK', 'zh_TW', 'zh_HK'];
    if (currentLanguages.includes(lang)) {
        return `languages.${lang}`;
    }
    return language || '-';
};

/**
 * 打开预览页面
 * @param plugin_id 插件 id
 * @param plugin_version 插件版本（0-旧版插件 1-新版插件）
 */
export function openPluginPreview(plugin_id: string, plugin_version: '0' | '1' = '0') {
    const url = plugin_version === '1' ? '/next/preview/' : '/preview/';
    window.open(`${url}?id=${plugin_id}&ot=${Base64.Base64.encode(Date.now().toString())}`, isClient() ? '' : 'ss_preview_new');
}

// 打开测试页面
export function openFlowTest(plugin_id: string, flow_id: string, from: string) {
    window.open(`/sandbox/?pid=${plugin_id}&fid=${flow_id}&r=${from}&ot=${Base64.Base64.encode(Date.now().toString())}`, isClient() ? '' : 'ss-sandbox-chat', 'left=100,top=50,width=800,height=700');
}

/**
 * 生成随机的英文
 * @param len 长度
 * @returns {String}
 */
export function generateRandomEnName(len: number = 8): string {
    const chars: string[] = [];
    for (let i = 0; i < len; i += 1) {
        chars.push(String.fromCharCode(random(97, 122)));
    }

    return chars.join('');
}

/**
 * 生成随机的数字
 * @param len 长度
 * @returns {String}
 */
export const generateRandomNumber = utils.generateRandomNumber;

// 获取时区
export function getLocalTimeZone() {
    // 如果存在于列表中则拿列表中的，如果不存在则取同时区的第一个时区
    const localGmt: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localZt: string = dayjs(new Date()).format('Z');

    const list: any[] = getTimeZone();

    const checkItem = list.filter((i: any) => i.gmt === localGmt);

    if (checkItem.length > 0) {
        // 取出匹配的
        return {
            tz: localZt,
            gmt: localGmt,
        };
    }
    // 如果没有匹配上则取出同时区中的第一个
    const gmts: any[] = list.filter(
        (i: any) => i.tz.indexOf(localZt) >= 0,
    );

    if (gmts && gmts.length > 0) {
        return {
            tz: localZt,
            gmt: gmts[0].gmt,
        };
    }
    // 如果都没有匹配则返回默认时区
    return { tz: '+00:00', gmt: 'Europe/London' };
}

// 号码去除 + - 空格
export const trimPhoneNumber = utils.trimPhoneNumber;

export const convertObj = (data: any) => {
    const _result: string[] = [];
    // eslint-disable-next-line guard-for-in
    for (const key in data) {
        const value = data[key];
        if (value.constructor === Array) {
            value.forEach((_value: any) => {
                _result.push(`${key}=${_value}`);
            });
        } else {
            _result.push(`${key}=${value}`);
        }
    }
    return _result.join('&');
};

export const ua = window.navigator.userAgent.toLowerCase();
export const checkMobile = () => window.outerWidth < 980 && /mobile|android|iphone|ipad|phone/i.test(ua);

/**
 * 清理并格式化 json 字符串
 * @param jsonString
 */
export const cleanAndFormatJsonString = utils.cleanAndFormatJsonString;

/**
 * 根据文件大小获取http请求超时时间
 * @param bytes 媒体资源大小（字节）
 * @returns
 */
const getTimeByFileSize = (bytes: number) => {
    if (bytes) {
        // 1M及以下 20s;
        // 2M及以下 40s -> 120s
        // 2-10M 80s -> 120s
        // 10M以上 120s
        const size = Math.ceil(bytes / (1024 * 1024));
        console.log('fileSize', size);
        if (size <= 1) {
            return 20;
        }
        if (size <= 2) {
            return 40;
        }
        if (size <= 10) {
            return 80;
        }
        return 120;
    }
    // 默认120s
    return 120;
};

/**
 * 递归上传方法
 * @param retryConfig 上传配置
 * @param file 文件对象
 * @param switchTime 是否根据文件大小切换时间
 */
export const recursionUpload = (
    retryConfig: {
        fn: Function,
        errInfo: any[] | unknown,
        params: any;
    }[],
    file: File,
    switchTime: boolean = false,
) => {
    return new Promise((resolve, reject) => {
        let _m = 0;
        const _msg: any = [];
        const upload_file: any = {
            size: file?.size,
            name: file?.name,
            type: file?.type,
        };

        if (upload_file.size) {
            // 增加kb数据
            upload_file.size_kb = `${(Number(upload_file.size) / 1024).toFixed(2)}KB`;
        }

        // 超时时长，单位秒
        let timeout = switchTime ? getTimeByFileSize(upload_file.size) : 120;

        const recursion = (apiKey: number, uploadTime: number = 120) => {
            const { fn, errInfo, params } = retryConfig[apiKey];
            // 当前时间戳
            const currentTime = dayjs().valueOf();
            fn({ ...params, timeout: uploadTime }).then((res: any) => {
                resolve(res);
                if (apiKey > 0) {
                    _msg.push({ response: res, upload_time: dayjs().valueOf() - currentTime });
                    Vue.prototype.$log('SS_OSS', 'SS_OSS_UPLOAD_RETRY_SUCCESS', { data: { err: _msg, upload_file, count: _msg.length } });
                }
            }).catch((uploadErr: any) => {
                let err = uploadErr;
                // console.log('recursion====>>>>>>>>', err.code, err.status, err.name, fn);
                try {
                    if (err && Array.isArray(err)) {
                        err = err[0]?.response || {};
                    }
                    if (err && err.params && err.params.content && err.params.content.type === 'Buffer') {
                        // 如果响应错误内容包含buffer内容，则剔除此部分的内容
                        err.params.content.data = 'Buffer data';
                    }
                    if (err && typeof err === 'object') {
                        const { params: otherParams, ...other } = err;
                        _msg.push({ response: other, upload_time: dayjs().valueOf() - currentTime });
                    } else {
                        _msg.push({ response: err, upload_time: dayjs().valueOf() - currentTime });
                    }

                    if (!errInfo || (errInfo as any[]).some((info: { status: number, code: string }) => (info.code === err?.code && info.status === err?.status))) {
                        // 匹配上错误原因或者没有配置错误原因的继续重试
                        _m += 1;
                        if (_m + 1 <= retryConfig.length) {
                            if (switchTime && uploadTime !== 120 && err?.code === 'ConnectionTimeoutError' && err?.name === 'ConnectionTimeoutError') {
                                // 请求超时时，改变请求时间，并重新尝试
                                _msg.push({ code: 'upload-timeout', errMsg: '上传资源，请求超时', time: timeout });
                                timeout = 120;
                            }
                            recursion(_m, timeout);
                        } else {
                            reject(err);
                            Vue.prototype.$log('SS_OSS', 'SS_OSS_UPLOAD_RETRY_FAIL', { data: { err: _msg, upload_file, count: _msg.length } });
                        }
                    } else {
                        reject(err);
                        Vue.prototype.$log('SS_OSS', 'SS_OSS_UPLOAD_RETRY_FAIL', { data: { err: _msg, upload_file, count: _msg.length } });
                    }
                } catch (error) {
                    reject(err);
                    Vue.prototype.$log('SS_OSS', 'SS_OSS_UPLOAD_RETRY_FAIL', { data: { err: _msg, upload_file, count: _msg.length, error } });
                }
            }).finally(() => {
                console.log('_msg=====>>>>>>>', JSON.stringify(_msg), upload_file);
            });
        };
        // 递归进行重试
        recursion(_m, timeout);
    });
};

type CompressUploadFileConfig = {
    file: File;
    quality?: number; // 压缩质量
    expandParams?: any; // 拓展参数
    compressionLogName?: string; // 压缩日志名称
    errorLogName?: string; // 错误日志名称
    targetCompressFileSize?: number; // 超过该大小进行压缩
    onSuccess: (compressFile: File, result?: File | Blob) => void;
    onError: (error: any) => void;
}

/**
 * 上传文件压缩
 * @param {CompressUploadFileConfig} config 配置项
 */
export const compressUploadFile = (
    {
        file,
        quality = 0.85,
        compressionLogName = '',
        errorLogName = '',
        targetCompressFileSize = 1024 * 1024, // 默认1m
        expandParams = null,
        onSuccess,
        onError,
    }: CompressUploadFileConfig,
) => {
    const originalFileSize = file.size;
    // 如果不需要压缩或格式不符，直接触发成功回调
    if (Number(originalFileSize) < targetCompressFileSize || UNCOMPRESSED_FORMAT_TYPE.includes(file.type)) {
        onSuccess(file);
        return;
    }
    let params = {
        quality,
    };
    if (expandParams) {
        params = { ...expandParams };
    }
    const compressorInstance = new Compressor(file, {
        ...params,
        success(result) {
            try {
                const compressFile = new File([result], file.name, { type: file.type });
                onSuccess(compressFile, result);
                const compressFileSize = compressFile.size;
                // 如果压缩后文件小于压缩前的一半, 记录日志
                if (compressionLogName && Number(compressFileSize) < Number(originalFileSize) / 2) {
                    Vue.prototype.$log('COMPRESS_UPLOAD_FILE_HALF', compressionLogName, {
                        data: {
                            file_name: file.name,
                            file_type: file.type,
                            file_size: file.size,
                            compressor_file_type: compressFile.type,
                            compress_file_size: compressFileSize,
                        },
                    });
                }
            } catch (err) {
                onError(err);
            }
        },
        error(err) {
            console.error(err);
            onError(err);
            if (errorLogName) {
                // 错误日志上报
                const errorData = {
                    error_msg: err.toString(),
                    file_name: file.name,
                    file_type: file.type,
                    file_size: file.size,
                };
                Vue.prototype.$log('COMPRESS_UPLOAD_FILE_ERROR', errorLogName, { data: errorData });
            }
        },
    });
};

/**
 * 获取当前时间偏移指定月数的秒级时间戳
 * @param {number} monthOffset - 月份的偏移量（负数表示之前，正数表示之后）
 * @returns {number} - 偏移后的日期时间戳（秒级）
 */
export const getTimestampWithMonthOffset = (monthOffset: number = 0): number => {
    const date = new Date();
    date.setMonth(date.getMonth() + monthOffset);
    return Math.floor(date.getTime() / 1000);
};

/**
 * 匹配所有需要转义的特殊字符
 * @param str
 */
export const escapeSpecialChars = (str: string) => {
    return str.replace(/[-\\/\\^$*+?.()|[\]{}]/g, '\\$&');
};

/**
 * 获取视频截图地址 - 详细oss文档：https://www.alibabacloud.com/help/zh/oss/user-guide/video-snapshots?spm=a2c63.p38356.0.0.41063bd4jZPXDS#section-j0q-cr3-k06
 * @param videoUrl
 * @param t 帧数，ms
 * @param w 指定截图宽度，如果指定为0，则根据截图高度与源视频高度比例自动计算。
 * @param h 指定截图高度，如果指定为0，则根据截图宽度与源视频宽度比例自动计算；如果w和h都为0，则输出为原视频宽高
 * @param f 指定输出图片的格式。
 * @returns {String} url
 */
export const getVideoSnapshotUrl = (videoUrl: string, w = 0, h = 0, f = 'png', t = 1) => {
    if (!videoUrl) return '';
    const url = replaceAcceleratedDomain(videoUrl).replace('http://', 'https://');
    const param = `x-oss-process=video/snapshot,t_${t},f_${f},w_${w},h_${h}`;
    if (url.includes('?')) {
        return `${url}&${param}`;
    }
    return `${url}?${param}`;
};

const checkValidDecodedString = (decodedString: string) => {
    try {
        // 尝试将解码后的字符串转换为 UTF-8 字符串
        decodeURIComponent(escape(decodedString));
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * 获取分享链接的token
 * @returns {string} shareAccessToken
 */
export const getShareChannelAccessToken = (id: string) => {
    try {
        const localToken = localRead(`${SS_SHARE_CHANNEL_ACCESS_TOKEN_KEY}_${id}`);
        if (localToken) {
            const shareAccessToken = Base64.Base64.decode(localToken);
            return checkValidDecodedString(shareAccessToken) ? shareAccessToken : '';
        }
        return '';
    } catch (error) {
        return '';
    }
};

/** SSO start */
export const saveSSOInfo = (info: any) => {
    sessionSave(
        THIRD_PARTY_SSO_K_I,
        Base64.Base64.encode(JSON.stringify(info)),
    );
};

export const readSSOInfo = () => {
    try {
        const localInfo = sessionRead(THIRD_PARTY_SSO_K_I);
        if (localInfo) {
            return JSON.parse(Base64.Base64.decode(localInfo));
        }
        return {};
    } catch (err) {
        return {};
    }
};

/**
 * 检查当前是否在iframe内
 */
export const checkInIframe = () => window.parent !== window;

export const isSSO = () => {
    return checkInIframe() && readSSOInfo().page_model;
};

export const goToRedirectPage = () => {
    const { redirect_url } = readSSOInfo();
    if (redirect_url) {
        window.location.replace(redirect_url);
    } else {
        const { origin } = window.location;
        window.location.replace(`${origin}/auth/sso_failed`);
    }

    sessionRemove(THIRD_PARTY_SSO_K_I);
};

export const checkHideSomething = () => {
    return readSSOInfo().page_model === '1';
};

/** SSO end */

export const extractBracedContent = (input: string) => {
    // 定义正则表达式，匹配 {{ 和 }} 之间的内容
    const regex = /\{\{(.*?)\}\}/g;
    let match;
    const results = [];

    // 使用正则表达式的 exec 方法在字符串中查找所有匹配项
    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(input)) !== null) {
        // match[1] 包含 {{ 和 }} 之间的内容
        results.push(`{{${match[1]}}}`);
    }

    return results;
};
